import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * Discord Community CTA button — ALWAYS routes through the OAuth-based
 * "Connect Discord" flow. There are no raw `discord.gg` invite paths
 * anywhere in the UI, on purpose — every visitor either creates an
 * account first (unauth) or hits the Account page's Connect button
 * (auth). Linked members land on the same page and see an "Open Server"
 * shortcut. Zero orphan paths, zero role-less joins.
 *
 * Destinations by auth state:
 *  - Unauthenticated  → `/onboarding` (free-account quiz, then Connect)
 *  - Auth, unlinked   → `/account?tab=discord` (Connect Discord panel)
 *  - Auth, linked     → `/account?tab=discord` (same page — shows Open Server)
 *
 * Visual variants:
 *  - "solid" (default): Discord indigo (#5865F2) filled
 *  - "outline"        : Bordered on dark backgrounds
 *  - "ghost"          : Subtle chip for inline placements
 */
export default function DiscordCTA({
  variant = "solid",
  size = "md",
  label = "Connect Discord community",
  className = "",
  testId = "discord-cta",
  // Kept as a prop for API compatibility but no longer changes routing —
  // the OAuth-only flow already collapses "join" and "link" into one path.
  // eslint-disable-next-line no-unused-vars
  signupFirst = false,
}) {
  const { user } = useAuth();

  const sizeCls =
    size === "sm" ? "h-9 px-3.5 text-xs"
    : size === "lg" ? "h-12 px-7 text-sm"
    : "h-11 px-5 text-sm";

  const variantCls =
    variant === "outline"
      ? "border border-white/25 text-white hover:bg-white/10 bg-transparent"
      : variant === "ghost"
      ? "text-[#5865F2] hover:bg-[#5865F2]/10 bg-transparent"
      : "bg-[#5865F2] hover:bg-[#4752C4] text-white shadow-sm hover:shadow";

  const cls = `inline-flex items-center gap-2 rounded-full font-semibold transition-colors ${sizeCls} ${variantCls} ${className}`;

  // Discord glyph
  const DiscordGlyph = (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width="16"
      height="16"
      className="shrink-0 fill-current"
    >
      <path d="M20.317 4.369A19.79 19.79 0 0 0 16.558 3c-.177.318-.383.744-.526 1.083a18.32 18.32 0 0 0-5.062 0A12.32 12.32 0 0 0 10.44 3a19.72 19.72 0 0 0-3.76 1.369C3.204 8.66 2.418 12.833 2.81 16.943a19.9 19.9 0 0 0 6.09 3.086c.494-.672.933-1.386 1.31-2.132a12.87 12.87 0 0 1-2.062-.988c.173-.127.341-.26.505-.397 3.945 1.822 8.213 1.822 12.11 0 .166.137.334.27.505.397a12.9 12.9 0 0 1-2.065.989c.377.746.815 1.46 1.31 2.131a19.86 19.86 0 0 0 6.093-3.085c.457-4.775-.783-8.912-3.29-12.574ZM9.681 14.55c-1.183 0-2.152-1.086-2.152-2.418 0-1.333.954-2.42 2.152-2.42 1.204 0 2.166 1.094 2.152 2.42.001 1.332-.948 2.418-2.152 2.418Zm7.955 0c-1.183 0-2.152-1.086-2.152-2.418 0-1.333.954-2.42 2.152-2.42 1.204 0 2.166 1.094 2.152 2.42 0 1.332-.948 2.418-2.152 2.418Z" />
    </svg>
  );

  const destination = user ? "/account?tab=discord" : "/onboarding?src=discord-cta";

  return (
    <Link
      to={destination}
      data-testid={testId}
      className={cls}
    >
      {DiscordGlyph}
      <span className="whitespace-nowrap">{label}</span>
      <ExternalLink size={13} className="opacity-70" />
    </Link>
  );
}
